import { renderIf, html, css } from '../../utility/lit-entity';
import { sharedStyles } from '../../styles/shared-styles';
import { BnAction } from '../../common/bn-action';

class OrganizationAddChild extends BnAction {
  static get properties() {
    return {
      rootEntity: { type: Object, attribute: false },
      integrationsEntity: { type: Object, attribute: false },
      hubspotIntegrationsEntity: { type: Object, attribute: false },
      hubspotCompanies: { type: Object, attribute: false },
      matchingHubspotCompany: { type: Object, attribute: false },
    };
  }

  static get styles() {
    return [sharedStyles, css`
      .map-to-hubspot {
        display: inline-flex;
        align-items: center;
      }
    `];
  }

  render() {
    const config = JSON.parse(import.meta.env.VITE_PROJECT_AWS_SSM_CONFIG);
    const apiRoot = config['api-root'];
    return html`
        <lit-entity .href="${apiRoot}" @update="${e => this.rootEntity = e.detail.entity}"></lit-entity>
        ${renderIf(this.entity && this.rootEntity, () => super.render())}
    `;
  }

  renderField(field) {
    const hasIntegrationPermission = !!this.rootEntity.getLink('https://api.binsentry.com/rel/integrations');
    if (!(this._isMappableInHubspot() && field.name === 'name') || !hasIntegrationPermission) {
      return super.renderField(field);
    }
    const options = this.hubspotCompanies?.map(entity => ({ value: entity.properties.hubspotCompanyName })) || [];

    const integrationsHref = this.rootEntity?.getLink('https://api.binsentry.com/rel/integrations').href;
    const hubspotHref = this.integrationsEntity?.getSubEntity('https://api.binsentry.com/rel/hubspot-integration').href;
    const hubspotCompaniesHref = this.hubspotIntegrationsEntity?.getLink('https://api.binsentry.com/rel/hubspot-companies').href;

    return html`
      <lit-entity .href="${integrationsHref}" @update="${e => this.integrationsEntity = e.detail.entity}"></lit-entity>
      <lit-entity .href="${hubspotHref}" @update="${e => this.hubspotIntegrationsEntity = e.detail.entity}"></lit-entity>
      <lit-entity .href="${hubspotCompaniesHref}" @update="${e => this.hubspotCompanies = e.detail.entity?.getSubEntities('item')}"></lit-entity>

      <div style="max-width: 50em">Options are suggested from Hubspot companies that are not yet mapped to a Binsentry organization. If you map this
          new organization, information will be synced, including all associated users and bin installation counts.</div>
      <bn-form-field
        ?required="${field?.class.includes('required')}"
        .name="${field.name}"
        .title="${field.title}"
        .type="${field.type}"
        .value="${field.value}"
        .options="${options}"
        @change="${this._nameChanged}"
        @keyup="${this._nameChanged}"
        @paste="${this._nameChanged}"
        @cut="${this._nameChanged}"
        @blur="${this._nameChanged}"
        @input="${this._nameChanged}"
        autocomplete="off"
      ></bn-form-field>
      <label class="map-to-hubspot">
        <input
          type="checkbox"
          name="mapToHubspot"
          title="Map to Hubspot?"
          value="true"
          ?disabled="${!this.matchingHubspotCompany}"
        ></input>
        <span>Map to Hubspot?</span>
      </label>
    `;
  }

  async _submit(e) {
    const result = await super._submit(e);
    if (!result || result.error) {
      return;
    }

    const mappedInHubspot = this._isMappableInHubspot() && this.renderRoot.querySelector('input[name=mapToHubspot]').checked;
    if (mappedInHubspot) {
      const action = this.hubspotIntegrationsEntity.getAction('link-organization-to-hubspot');
      const organizationId = result.entity.getLink('self').href;
      try {
        await this.submitAction(action, {
          hubspotCompanyId: this.matchingHubspotCompany.properties.hubspotCompanyId,
          organizationId,
        });
      } catch (e) {
        this._getFlash().critical(html`Failed to link new organization in hubspot: ${e.message}<br/>
          To fix this, you can manually link the organization in hubspot by setting the binsentry_organization_id to ${organizationId}<br/>
          <a href="/organizations/list?orgId=${organizationId}">Continue to ${result.entity.properties.name}</a> 
        `);
        return;
      }
    }
    this.dispatchEvent(new CustomEvent('organization-added', { detail: { ...result, mappedInHubspot }, bubbles: true, composed: true }));
  }

  _nameChanged(e) {
    this.matchingHubspotCompany = this.hubspotCompanies?.find(entity => entity.properties.hubspotCompanyName === e.target.input.value);
    const mapToHubspotField = this.renderRoot.querySelector('input[name=mapToHubspot]');
    mapToHubspotField.checked = !!this.matchingHubspotCompany;
    this._checkMissingFields();
  }

  _isMappableInHubspot() {
    return ['create-account', 'create-billing-account'].includes(this.actionname);
  }
}

window.customElements.define('organization-add-child-action', OrganizationAddChild);
